import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { useBusStatusEvents } from './useBusStatusEvents'
import type { BusBoardRow } from './types'

/** Step 1 of the arrival/departure ask: "who's in Khapri/Wathoda right now, since when" --
 *  radius-around-point presence (see serving/app.py's GET /api/bus/board comment), not
 *  MBMT-style LATE/EARLY (that needs a duty schedule source Nagpur doesn't have yet --
 *  Step 2, blocked on an external input, not code). Status/position update live via
 *  useBusStatusEvents (SSE, see that hook's own comment); the EDMS-duty fields SSE does
 *  not carry (and the resync-if-SSE-drops-something safety net) come from `board`/
 *  `boardLoaded` props now, not a fetch of this component's own -- NagpurBusOpsShell
 *  already polls the identical GET /api/bus/board every FLEET_POLL_MS (15s) for the
 *  Fleet drawer, strictly more often than this board's former 120s interval, so a
 *  second poll of the same endpoint here only ever duplicated a request for staler data
 *  (CLAUDE.md's live-data section). */

// Same threshold as NagpurBusOpsShell.tsx's own FLEET_STALE_MS and serving/app.py's
// NAGPUR_PRESENCE_STALE_MS. Confirmed previously a real gap: useBusStatusEvents' `live`
// map only ever accumulates one entry per plate and never expires it, and the merge
// below unconditionally preferred that cached SSE event over the poll's own (now
// correctly staleness-checked server-side) status -- so a bus that sent one telemetry
// frame and then went dark kept showing that frame's status forever on THIS board,
// even after the backend started reporting no_signal on its next poll.
const LIVE_EVENT_STALE_MS = 5 * 60_000

const DEPOT_LABEL: Record<string, string> = { wathoda: 'Wathoda', khapri: 'Khapri' }

const STATUS_LABEL: Record<BusBoardRow['status'], string> = {
  in_depot: 'In depot',
  out_on_route: 'Out on route',
  no_signal: 'No signal',
}

const STATUS_DOT: Record<BusBoardRow['status'], string> = {
  in_depot: 'bg-[#047857]',
  out_on_route: 'bg-[#0369A1]',
  no_signal: 'bg-[#94A3B8]',
}

function durationSince(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}

/** EDMS duty join (serving/app.py's _nagpur_duty_for/_nagpur_current_trip) -- null when
 *  this plate has no duty row today, same "render nothing extra" the board API's own
 *  "null, not omitted" convention implies (AC-3). */
function dutyLine(r: BusBoardRow): string | null {
  const parts: string[] = []
  if (r.driver_name) parts.push(r.driver_name)
  if (r.conductor_id) parts.push(`Cond ${r.conductor_id}`)
  if (r.schedule_code) parts.push(r.schedule_code + (r.schedule_shift ? ` (${r.schedule_shift})` : ''))
  if (r.current_trip) parts.push(`${r.current_trip.from_stage ?? '?'} → ${r.current_trip.to_stage ?? '?'}`)
  // SPEC.md's own edge case: two schedules can claim the same plate/day. schedule_code
  // above only ever shows the single primary pick -- this is where the second one
  // actually becomes visible instead of silently disappearing from the depot board.
  if (r.schedules.length > 1) parts.push(`+${r.schedules.length - 1} more schedule${r.schedules.length > 2 ? 's' : ''}`)
  return parts.length ? parts.join(' · ') : null
}

export function BusDepotBoard(
  { board, boardLoaded, onSelectVehicle }: {
    board: BusBoardRow[]
    boardLoaded: boolean
    onSelectVehicle?: (id: string) => void
  },
): JSX.Element {
  const [filter, setFilter] = useState<'all' | BusBoardRow['status']>('all')
  const live = useBusStatusEvents()
  // vehicle_id -> since_ts, bumped to "now" only when a live event actually flips that
  // vehicle's status -- the poll's own since_ts (server-persisted, see
  // serving/app.py's _nagpur_presence_since) stays authoritative otherwise, and a fresh
  // poll response always wins once it arrives.
  const sinceOverride = useRef<Record<string, number>>({})
  const lastLiveStatus = useRef<Record<string, BusBoardRow['status']>>({})

  // A fresh board poll is authoritative -- drop any override for a vehicle whose polled
  // status already agrees with it, so a stale client-side guess never outlives the
  // server's own next real answer. Used to run inside this component's own poll
  // .then(); board is a prop now, so this just reacts to it changing instead.
  useEffect(() => {
    for (const row of board) {
      if (lastLiveStatus.current[row.vehicle_id] === row.status) {
        delete sinceOverride.current[row.vehicle_id]
      }
    }
  }, [board])

  const merged = board.map((r) => {
    const ev = live[r.vehicle_id]
    // A live event this old is no more trustworthy than the stale nagpur_vehicles entry
    // it came from -- fall back to the poll's own row, which the backend's own staleness
    // guard (serving/app.py's _nagpur_presence) keeps correct independently of whether
    // SSE delivered anything recently.
    if (!ev || ev.updated_ts == null || Date.now() - ev.updated_ts > LIVE_EVENT_STALE_MS) return r
    if (lastLiveStatus.current[r.vehicle_id] !== ev.status) {
      lastLiveStatus.current[r.vehicle_id] = ev.status
      if (ev.status !== r.status) sinceOverride.current[r.vehicle_id] = Date.now()
    }
    return {
      ...r,
      status: ev.status,
      depot_id: ev.depot_id,
      distance_km: ev.distance_km,
      lat: ev.lat,
      lon: ev.lon,
      speed_kph: ev.speed_kph,
      heading_deg: ev.heading_deg,
      soc_pct: ev.soc_pct,
      updated_ts: ev.updated_ts,
      since_ts: sinceOverride.current[r.vehicle_id] ?? r.since_ts,
    }
  })

  const counts = {
    in_depot: merged.filter((r) => r.status === 'in_depot').length,
    out_on_route: merged.filter((r) => r.status === 'out_on_route').length,
    no_signal: merged.filter((r) => r.status === 'no_signal').length,
  }
  const shown = filter === 'all' ? merged : merged.filter((r) => r.status === filter)

  if (!boardLoaded) return <p className="p-4 text-[12px] text-[#475569]">Loading depot board…</p>

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-1 border-b border-[rgba(15,23,42,0.06)] px-2 py-1.5">
        {([['all', `All ${merged.length}`], ['in_depot', `In depot ${counts.in_depot}`],
          ['out_on_route', `Out ${counts.out_on_route}`],
          ['no_signal', `No signal ${counts.no_signal}`]] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setFilter(id)}
            className={clsx(
              'rounded-full px-2 py-0.5 text-[10.5px] font-semibold transition-colors',
              filter === id ? 'bg-[#0F172A] text-white' : 'text-[#64748B] hover:bg-[#EEF2F7]',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {shown.length === 0 && (
          <p className="px-3 py-3 text-[12px] text-[#94A3B8]">Nothing in this lane right now.</p>
        )}
        {shown.map((r) => {
          const duty = dutyLine(r)
          return (
            <button
              key={r.vehicle_id}
              type="button"
              onClick={() => onSelectVehicle?.(r.vehicle_id)}
              className="block w-full border-b border-[rgba(15,23,42,0.04)] px-3 py-1.5 text-left transition-colors hover:bg-[#F8FAFC]"
            >
              <div className="flex items-center gap-2">
                <span className={clsx('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT[r.status])} />
                <span className="font-mono text-[12px] font-semibold text-[#0F172A]">{r.vehicle_id}</span>
                <span className="text-[11px] text-[#64748B]">
                  {STATUS_LABEL[r.status]}
                  {r.depot_id ? ` · ${DEPOT_LABEL[r.depot_id] ?? r.depot_id}` : ''}
                </span>
                <span className="ml-auto font-mono text-[10.5px] tabular-nums text-[#94A3B8]">
                  {durationSince(r.since_ts)}
                </span>
              </div>
              {duty ? (
                <div className="truncate pl-3.5 text-[10.5px] text-[#94A3B8]">{duty}</div>
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}
